/**
 * categoryIcons — single source of truth mapping a category slug (e.g.
 * "alimentos", "beleza") to its lucide-react icon component.
 *
 * Why a separate file: CategoryCard already had the lucide imports but keyed
 * by the `icone` string field returned by the backend. Other components like
 * BusinessCard only know the category slug/id, so they need a slug-based map.
 * Both keep in sync via this module.
 */
import {
  UtensilsCrossed, Sparkles, Heart, Wrench, ShoppingBag, HardHat,
  GraduationCap, Tractor, Palmtree, Car, Laptop, Banknote, Scale,
  Building, Truck, PartyPopper, Home, Dog, Factory, Radio, Building2,
  Dumbbell, Paintbrush, Palette, Shirt, Recycle, Shield, Package,
  Flower2, Briefcase, Megaphone, Hammer,
} from 'lucide-react';

// Lucide-name → component (matches `icone` field stored on each category)
export const ICONS_BY_NAME = {
  UtensilsCrossed, Sparkles, Heart, Wrench, ShoppingBag, HardHat,
  GraduationCap, Tractor, Palmtree, Car, Laptop, Banknote, Scale,
  Building, Truck, PartyPopper, Home, Dog, Factory, Radio, Building2,
  Dumbbell, Paintbrush, Palette, Shirt, Recycle, Shield, Package,
  Flower2, Briefcase, Megaphone, Hammer,
};

// Category slug → component
export const ICONS_BY_SLUG = {
  // Legacy (v1)
  agronegocio: Tractor,
  alimentos: UtensilsCrossed,
  artesanato: Paintbrush,
  artistas: Palette,
  automotivo: Car,
  beleza: Sparkles,
  casa: Home,
  compras: ShoppingBag,
  construcao: HardHat,
  educacao: GraduationCap,
  esportes: Dumbbell,
  eventos: PartyPopper,
  financeiro: Banknote,
  imobiliario: Building,
  industria: Factory,
  juridico: Scale,
  midia: Radio,
  pets: Dog,
  saude: Heart,
  servicos: Wrench,
  publicos: Building2,
  tecnologia: Laptop,
  transporte: Truck,
  turismo: Palmtree,
  // Taxonomia v2 (2026) — slugs curtos
  'alimentacao-bebidas': UtensilsCrossed,
  'moda-acessorios': Shirt,
  'compras-varejo': ShoppingBag,
  'esportes-bem-estar': Dumbbell,
  'servicos-pessoais': Wrench,
  'manutencao-reparos': Hammer,
  'construcao-engenharia': HardHat,
  'tecnologia-telecom': Laptop,
  'comunicacao-midia': Megaphone,
  'transporte-logistica': Truck,
  'turismo-hospedagem': Palmtree,
  'eventos-festas': PartyPopper,
  'pets-veterinaria': Dog,
  'casa-jardim': Home,
  'financeiro-seguros': Banknote,
  'juridico-documentacao': Scale,
  imoveis: Building,
  'consultoria-empresarial': Briefcase,
  'artes-cultura': Palette,
  'meio-ambiente': Recycle,
  seguranca: Shield,
  locacao: Package,
  funerarios: Flower2,
};

// Common backend display-name variations → canonical slug. The backend
// historically stored `categoria` as a free-form display string ("Saúde",
// "Alimentação", "Comércio") rather than the slug — this map keeps the
// resolver tolerant to that legacy.
const ALIAS_TO_SLUG = {
  agronegocio: 'agronegocio',
  agro: 'agronegocio',
  alimentos: 'alimentos',
  alimentacao: 'alimentos',
  comida: 'alimentos',
  artesanato: 'artesanato',
  artistas: 'artistas',
  arte: 'artistas',
  automotivo: 'automotivo',
  beleza: 'beleza',
  estetica: 'beleza',
  casa: 'casa',
  jardim: 'casa',
  compras: 'compras',
  comercio: 'compras',
  varejo: 'compras',
  construcao: 'construcao',
  reformas: 'construcao',
  educacao: 'educacao',
  ensino: 'educacao',
  esportes: 'esportes',
  esporte: 'esportes',
  eventos: 'eventos',
  festas: 'eventos',
  financeiro: 'financeiro',
  seguros: 'financeiro',
  imobiliario: 'imobiliario',
  imoveis: 'imobiliario',
  industria: 'industria',
  manufatura: 'industria',
  juridico: 'juridico',
  midia: 'midia',
  comunicacao: 'midia',
  pets: 'pets',
  veterinaria: 'pets',
  saude: 'saude',
  servicos: 'servicos',
  publicos: 'publicos',
  ongs: 'publicos',
  tecnologia: 'tecnologia',
  telecom: 'tecnologia',
  transporte: 'transporte',
  logistica: 'transporte',
  turismo: 'turismo',
  lazer: 'turismo',
};

function normalize(str) {
  return String(str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .trim();
}

/**
 * Resolve a category icon from either the slug ("alimentos"), display name
 * ("Saúde", "Alimentação"), or the lucide name string ("UtensilsCrossed").
 * Falls back to Building2 when nothing matches.
 */
export function getCategoryIcon(categoryKey) {
  if (!categoryKey) return Building2;
  // 1) lucide name as-is
  if (ICONS_BY_NAME[categoryKey]) return ICONS_BY_NAME[categoryKey];
  // 2) normalized slug (handles "Saúde" → "saude", "Comércio" → "comercio")
  const norm = normalize(categoryKey);
  if (ICONS_BY_SLUG[norm]) return ICONS_BY_SLUG[norm];
  // 3) alias map (handles "comercio" → "compras", "alimentacao" → "alimentos")
  const aliased = ALIAS_TO_SLUG[norm];
  if (aliased && ICONS_BY_SLUG[aliased]) return ICONS_BY_SLUG[aliased];
  return Building2;
}

export default getCategoryIcon;
